/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { gql, useMutation } from '@apollo/client'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'

import {
  ToggleFollowEntityMutation,
  ToggleFollowEntityMutationVariables,
  UseProfileFollow_ProfileFragment,
} from './__generated__/use-profile-follow.graphql'

export type UseProfileFollowProps = {
  profile?: UseProfileFollow_ProfileFragment | null
}

export function useProfileFollow(props: UseProfileFollowProps) {
  const { profile } = props

  const [mutate, { loading, data }] = useMutation<
    ToggleFollowEntityMutation,
    ToggleFollowEntityMutationVariables
  >(
    ToggleFollowMutation,
    profile
      ? {
          variables: {
            input: {
              entityId: profile.entityId!,
              entityType: profile.entityType!,
              action: profile.isFollowedByViewer ? 'UNFOLLOW' : 'FOLLOW',
            },
          },
          update(cache, { data }) {
            const { followedEntity } = data?.toggleFollowEntity || {}
            if (followedEntity) {
              cache.modify({
                id: cache.identify(profile),
                fields: {
                  isFollowedByViewer() {
                    return followedEntity.isFollowedByViewer
                  },
                  followersAmount() {
                    return followedEntity.followersAmount
                  },
                },
              })
            }
          },
        }
      : undefined
  )

  const { toggleFollowEntity } = data || {}

  const isFollowing =
    toggleFollowEntity?.followedEntity?.isFollowedByViewer ?? profile?.isFollowedByViewer ?? false

  return {
    isFollowing,
    loading,
    async toggle() {
      if (!profile) {
        throw new Error('Profile is required')
      }

      const eventName = profile.isFollowedByViewer ? 'Unfollow' : 'Follow'
      sendMetrics(`Profile.${eventName}`, null, {
        from_component: 'FollowButton',
        type: profile.entityType,
      })

      await mutate()
    },
  }
}

useProfileFollow.fragments = {
  profile: gql`
    fragment UseProfileFollow_profile on Profile {
      id
      __typename
      entityId
      entityType
      isFollowedByViewer
    }
  `,
}

export const ToggleFollowMutation = gql`
  mutation toggleFollowEntity($input: ToggleFollowEntityInput!) {
    toggleFollowEntity(input: $input) {
      followedEntity {
        id
        __typename
        isFollowedByViewer
        followersAmount
      }
    }
  }
`
